import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment
} from '@mui/material';
import { Close, Settings } from '@mui/icons-material';

interface TablePropertiesModalProps {
    open: boolean;
    onClose: () => void;
    table: HTMLTableElement | null;
    onSave: (properties: TableProperties) => void;
}

export interface TableProperties {
    cellPadding: string;
    cellSpacing: string;
    borderWidth: string;
    borderStyle: string;
    borderColor: string;
    backgroundColor: string;
    alignment: 'left' | 'center' | 'right';
}

const TablePropertiesModal: React.FC<TablePropertiesModalProps> = ({
    open,
    onClose,
    table,
    onSave
}) => {
    const [properties, setProperties] = useState<TableProperties>({
        cellPadding: '8px',
        cellSpacing: '0px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#dddddd',
        backgroundColor: 'transparent',
        alignment: 'center'
    });

    useEffect(() => {
        if (table && open) {
            // Tentar inferir dos estilos atuais
            const computedTableStyle = window.getComputedStyle(table);

            let alignment: 'left' | 'center' | 'right' = 'center';
            const dataAlign = table.getAttribute('data-table-align');
            if (dataAlign === 'left' || dataAlign === 'center' || dataAlign === 'right') {
                alignment = dataAlign;
            } else if (table.style.marginLeft === '0px' || table.style.marginLeft === '0') {
                alignment = 'left';
            } else if (table.style.marginRight === '0px' || table.style.marginRight === '0') {
                alignment = 'right';
            }

            // Checar as células
            let cellPadding = '8px';
            const htmlCellPadding = table.getAttribute('cellpadding');
            const firstCell = table.querySelector('td, th') as HTMLElement;
            if (firstCell) {
                const cellStyle = window.getComputedStyle(firstCell);
                cellPadding = firstCell.style.padding ? firstCell.style.padding : (cellStyle.padding && cellStyle.padding !== '0px' ? cellStyle.padding : (htmlCellPadding ? htmlCellPadding + 'px' : '0px'));
            } else if (htmlCellPadding) {
                cellPadding = htmlCellPadding + 'px';
            }

            let cellSpacing = '0px';
            const htmlCellSpacing = table.getAttribute('cellspacing');
            if (table.style.borderSpacing) {
                cellSpacing = table.style.borderSpacing;
            } else if (computedTableStyle.borderSpacing && computedTableStyle.borderSpacing !== '0px' && computedTableStyle.borderSpacing !== '0px 0px') {
                cellSpacing = computedTableStyle.borderSpacing.split(' ')[0]; // pega só o primeiro valor
            } else if (htmlCellSpacing) {
                cellSpacing = htmlCellSpacing + 'px';
            }

            setProperties({
                cellPadding: cellPadding.replace(/px|em|rem|%/g, '').trim() || '0',
                cellSpacing: cellSpacing.replace(/px|em|rem|%/g, '').trim() || '0',
                borderWidth: (computedTableStyle.borderWidth || '1px').replace(/px|em|rem|%/g, '').trim() || '0',
                borderStyle: computedTableStyle.borderStyle !== 'none' ? computedTableStyle.borderStyle : 'solid',
                borderColor: computedTableStyle.borderColor || '#dddddd',
                backgroundColor: computedTableStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' ? computedTableStyle.backgroundColor : 'transparent',
                alignment
            });
        }
    }, [table, open]);

    const handleSave = () => {
        onSave(properties);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={{ zIndex: 10000 }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Settings sx={{ fontSize: 18 }} />
                    <Typography variant="subtitle1" fontWeight={600} fontSize="0.9rem">Propriedades da Tabela</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <Close sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ px: 1.5, py: 1 }}>
                <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="Padding interno"
                            value={properties.cellPadding}
                            onChange={(e) => setProperties({ ...properties, cellPadding: e.target.value })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                sx: { fontSize: '0.8rem', height: 32 },
                            }}
                            InputLabelProps={{ sx: { fontSize: '0.78rem' } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="Espaço entre células"
                            value={properties.cellSpacing}
                            onChange={(e) => setProperties({ ...properties, cellSpacing: e.target.value })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                sx: { fontSize: '0.8rem', height: 32 },
                            }}
                            InputLabelProps={{ sx: { fontSize: '0.78rem' } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="Largura da Borda"
                            value={properties.borderWidth}
                            onChange={(e) => setProperties({ ...properties, borderWidth: e.target.value })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                sx: { fontSize: '0.8rem', height: 32 },
                            }}
                            InputLabelProps={{ sx: { fontSize: '0.78rem' } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontSize: '0.78rem' }}>Estilo da Borda</InputLabel>
                            <Select
                                value={properties.borderStyle}
                                label="Estilo da Borda"
                                onChange={(e) => setProperties({ ...properties, borderStyle: e.target.value })}
                                sx={{ fontSize: '0.8rem', height: 32 }}
                            >
                                <MenuItem value="solid" sx={{ fontSize: '0.78rem', minHeight: 28 }}>Sólida</MenuItem>
                                <MenuItem value="dashed" sx={{ fontSize: '0.78rem', minHeight: 28 }}>Tracejada</MenuItem>
                                <MenuItem value="dotted" sx={{ fontSize: '0.78rem', minHeight: 28 }}>Pontilhada</MenuItem>
                                <MenuItem value="double" sx={{ fontSize: '0.78rem', minHeight: 28 }}>Dupla</MenuItem>
                                <MenuItem value="none" sx={{ fontSize: '0.78rem', minHeight: 28 }}>Nenhuma</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={600}>Cor da Borda</Typography>
                            <input
                                type="color"
                                value={properties.borderColor.startsWith('#') ? properties.borderColor : '#000000'}
                                onChange={(e) => setProperties({ ...properties, borderColor: e.target.value })}
                                style={{ width: 28, height: 20, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 3 }}
                            />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
                            {[
                                { color: '#000000', label: 'Preto' },
                                { color: '#666666', label: 'Cinza' },
                                { color: '#1976d2', label: 'Azul' },
                                { color: '#2e7d32', label: 'Verde' },
                                { color: '#d32f2f', label: 'Vermelho' },
                                { color: '#f57c00', label: 'Laranja' }
                            ].map((item) => (
                                <Button
                                    key={item.color}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setProperties({ ...properties, borderColor: item.color })}
                                    sx={{
                                        minWidth: 'auto',
                                        height: 26,
                                        backgroundColor: item.color,
                                        border: properties.borderColor === item.color ? '2px solid #000' : '1px solid #ccc',
                                        '&:hover': { backgroundColor: item.color, opacity: 0.8 }
                                    }}
                                    title={item.label}
                                />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={600}>Cor de Fundo</Typography>
                            <input
                                type="color"
                                value={properties.backgroundColor.startsWith('#') ? properties.backgroundColor : '#ffffff'}
                                onChange={(e) => setProperties({ ...properties, backgroundColor: e.target.value })}
                                style={{ width: 28, height: 20, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 3 }}
                            />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
                            {[
                                { color: 'transparent', label: 'Nenhuma' },
                                { color: '#f5f5f5', label: 'Cinza' },
                                { color: '#e3f2fd', label: 'Azul' },
                                { color: '#e8f5e8', label: 'Verde' },
                                { color: '#fff9c4', label: 'Amarelo' },
                                { color: '#fce4ec', label: 'Rosa' }
                            ].map((item) => (
                                <Button
                                    key={item.color}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setProperties({ ...properties, backgroundColor: item.color })}
                                    sx={{
                                        minWidth: 'auto',
                                        height: 26,
                                        backgroundColor: item.color === 'transparent' ? '#fff' : item.color,
                                        backgroundImage: item.color === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 50%, #ccc 50%, #ccc 75%, transparent 75%, transparent)' : 'none',
                                        backgroundSize: item.color === 'transparent' ? '8px 8px' : 'auto',
                                        border: properties.backgroundColor === item.color ? '2px solid #000' : '1px solid #ccc',
                                        '&:hover': { backgroundColor: item.color === 'transparent' ? '#fff' : item.color, opacity: 0.8 }
                                    }}
                                    title={item.label}
                                />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Alinhamento</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                                variant={properties.alignment === 'left' ? 'contained' : 'outlined'}
                                onClick={() => setProperties({ ...properties, alignment: 'left' })}
                                fullWidth
                                size="small"
                                sx={{ fontSize: '0.72rem', py: 0.4 }}
                            >
                                ⬅️ Esquerda
                            </Button>
                            <Button
                                variant={properties.alignment === 'center' ? 'contained' : 'outlined'}
                                onClick={() => setProperties({ ...properties, alignment: 'center' })}
                                fullWidth
                                size="small"
                                sx={{ fontSize: '0.72rem', py: 0.4 }}
                            >
                                ↔️ Centro
                            </Button>
                            <Button
                                variant={properties.alignment === 'right' ? 'contained' : 'outlined'}
                                onClick={() => setProperties({ ...properties, alignment: 'right' })}
                                fullWidth
                                size="small"
                                sx={{ fontSize: '0.72rem', py: 0.4 }}
                            >
                                ➡️ Direita
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 1.5, py: 0.8 }}>
                <Button onClick={onClose} size="small" sx={{ fontSize: '0.78rem' }}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary" size="small" sx={{ fontSize: '0.78rem' }}>
                    Aplicar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TablePropertiesModal;
